import {API_BASE_PATHS} from '../../api/apiBasePaths';
import {getCsrfHeaders} from '../axios/interceptors/csrfInterceptor';

export type RefreshOutcome = 'refreshed' | 'unauthorized' | 'failed';

export interface RefreshResult {
  outcome: RefreshOutcome;
  /**
   * Milliseconds until the new access token expires; null unless the refresh succeeded and the server said.
   */
  expiresInMs: number | null;
}

let inFlight: Promise<RefreshResult> | null = null;

/**
 * Calls /auth/refresh, sharing one request across concurrent callers so simultaneous 401s (or
 * the refresh timer plus a 401) don't race on WorkOS refresh-token rotation.
 */
export function refreshSession(): Promise<RefreshResult> {
  inFlight ??= doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Reads how long the new access token has left from the refresh response. A body that can't be read is
 * not a failed refresh (the cookies are already set), so it just yields null.
 */
async function readExpiresInMs(response: Response): Promise<number | null> {
  try {
    const body: unknown = await response.json();
    const expiresInMs = (body as {expiresInMs?: unknown} | null)?.expiresInMs;
    return typeof expiresInMs === 'number' ? expiresInMs : null;
  } catch {
    return null;
  }
}

async function doRefresh(): Promise<RefreshResult> {
  try {
    const response = await fetch(
      `${API_BASE_PATHS.entitlements.path}/auth/refresh`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...getCsrfHeaders(),
        },
      },
    );

    if (response.status === 401) {
      return {outcome: 'unauthorized', expiresInMs: null};
    }

    if (!response.ok) {
      console.error('Token refresh failed:', response.statusText);
      return {outcome: 'failed', expiresInMs: null};
    }

    return {outcome: 'refreshed', expiresInMs: await readExpiresInMs(response)};
  } catch (error) {
    console.error('Token refresh error:', error);
    return {outcome: 'failed', expiresInMs: null};
  }
}
